/**
 * PlastiQ AI — Part configuration.
 *
 * All QA data lives in a unified `qa_samples` table filtered by `part_id`.
 * This config maps slugs to part metadata and available measurement types.
 */

export interface PartConfig {
  slug: string;
  label: string;
  category: string;
  material: string;
  /** Available QA measurement types for SPC charts */
  measurements: MeasurementType[];
  /** Primary measurement for default SPC view */
  primaryMeasurement: MeasurementType;
}

export interface MeasurementType {
  key: string;
  label: string;
  unit: string;
  column: string; // qa_samples column name
}

// All possible measurement types
export const MEASUREMENTS = {
  weight_g: { key: "weight_g", label: "Part Weight", unit: "g", column: "weight_g" },
  dim_length_mm: { key: "dim_length_mm", label: "Length", unit: "mm", column: "dim_length_mm" },
  dim_width_mm: { key: "dim_width_mm", label: "Width", unit: "mm", column: "dim_width_mm" },
  dim_height_mm: { key: "dim_height_mm", label: "Height", unit: "mm", column: "dim_height_mm" },
  dim_diameter_mm: { key: "dim_diameter_mm", label: "Diameter", unit: "mm", column: "dim_diameter_mm" },
  dim_wall_thickness_mm: { key: "dim_wall_thickness_mm", label: "Wall Thickness", unit: "mm", column: "dim_wall_thickness_mm" },
  cycle_time_s: { key: "cycle_time_s", label: "Cycle Time", unit: "s", column: "cycle_time_s" },
  delta_e: { key: "delta_e", label: "Colour (ΔE)", unit: "", column: "delta_e" },
  cushion_mm: { key: "cushion_mm", label: "Cushion Position", unit: "mm", column: "cushion_mm" },
  pack_pressure_bar: { key: "pack_pressure_bar", label: "Pack Pressure", unit: "bar", column: "pack_pressure_bar" },
} as const;

const M = MEASUREMENTS;

export const PARTS: Record<string, PartConfig> = {
  "auto-clip-7a": {
    slug: "auto-clip-7a",
    label: "Auto Clip 7A",
    category: "Automotive",
    material: "PA6-GF30",
    measurements: [M.weight_g, M.dim_length_mm, M.dim_width_mm, M.cycle_time_s, M.delta_e],
    primaryMeasurement: M.weight_g,
  },
  "med-housing-3": {
    slug: "med-housing-3",
    label: "Medical Housing 3mm",
    category: "Medical",
    material: "PC/ABS",
    measurements: [M.weight_g, M.dim_length_mm, M.dim_width_mm, M.dim_height_mm, M.dim_wall_thickness_mm, M.cycle_time_s, M.delta_e],
    primaryMeasurement: M.weight_g,
  },
  "cable-gland-m20": {
    slug: "cable-gland-m20",
    label: "Cable Gland M20",
    category: "Industrial",
    material: "PA66",
    measurements: [M.weight_g, M.dim_diameter_mm, M.dim_height_mm, M.cycle_time_s],
    primaryMeasurement: M.weight_g,
  },
  "bottle-cap-30mm": {
    slug: "bottle-cap-30mm",
    label: "Bottle Cap 30mm",
    category: "Consumer",
    material: "HDPE",
    measurements: [M.weight_g, M.dim_diameter_mm, M.dim_height_mm, M.cycle_time_s, M.delta_e],
    primaryMeasurement: M.weight_g,
  },
  "gear-wheel-50t": {
    slug: "gear-wheel-50t",
    label: "Gear Wheel 50T",
    category: "Automotive",
    material: "POM",
    measurements: [M.weight_g, M.dim_diameter_mm, M.dim_height_mm, M.dim_wall_thickness_mm, M.cycle_time_s],
    primaryMeasurement: M.weight_g,
  },
  "connector-rj45": {
    slug: "connector-rj45",
    label: "RJ45 Connector Body",
    category: "Electronics",
    material: "ABS",
    measurements: [M.weight_g, M.dim_length_mm, M.dim_width_mm, M.dim_height_mm, M.cycle_time_s, M.delta_e],
    primaryMeasurement: M.weight_g,
  },
  "pipe-fitting-25": {
    slug: "pipe-fitting-25",
    label: "Pipe Fitting 25mm",
    category: "Industrial",
    material: "PP",
    measurements: [M.weight_g, M.dim_diameter_mm, M.dim_height_mm, M.dim_wall_thickness_mm, M.cycle_time_s],
    primaryMeasurement: M.weight_g,
  },
  "lens-cover-led": {
    slug: "lens-cover-led",
    label: "LED Lens Cover",
    category: "Consumer",
    material: "PMMA",
    measurements: [M.weight_g, M.dim_diameter_mm, M.dim_height_mm, M.dim_wall_thickness_mm, M.cycle_time_s, M.delta_e],
    primaryMeasurement: M.weight_g,
  },
};

export const PART_SLUGS = Object.keys(PARTS);

export function getPart(slug: string): PartConfig | null {
  return PARTS[slug] ?? null;
}

/**
 * Get the measurement config for a given measurement key.
 */
export function getMeasurement(key: string): MeasurementType | null {
  return (MEASUREMENTS as Record<string, MeasurementType>)[key] ?? null;
}
